import React, { useCallback, useContext } from 'react'

import { getOfferImageId, IS_SAVED_COPY, NOT_SAVED_COPY } from './utils'
import BaseBookmarkButton from '../Common/BaseBookmarkButton'
import SaveModal, { TripPlannerSaveModalResult } from '../Common/SaveModal'

import {
  saveToTripEvent,
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import {
  OfferTypeForTripItemTypes,
  useCreateTripItemBatch,
  useDeleteTripItem,
  useTrip,
} from 'tripPlanner/hooks/api'
import useBookmarkDetails from 'tripPlanner/hooks/bookmarks/useBookmarkDetails'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import {
  setCurrentSectionId,
  setTripItemHasJustBeenAdded,
} from 'tripPlanner/reducers/actions'
import {
  getImmersiveTripId,
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import {
  BasicTrip,
  CreateItemsBatchResponse,
  FullTrip,
} from 'tripPlanner/types/common'
import { createTourItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/tourAndCruises'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export interface Props {
  offer: Exclude<
    OfferTypeForTripItemTypes,
    App.BedbankOffer | App.BedbankOfferSummary | App.CruiseOffer
  >
  selectedVariation?: Tours.V2OfferVariation
  label?: string
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('save'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('save'))
}

function TourBookmarkButton({ offer, selectedVariation, label }: Props) {
  const currentTripId = useAppSelector(getImmersiveTripId)
  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const analyticsPage = useContext(AnalyticsPageContext)

  const details = useBookmarkDetails(offer.id)
  const isSaved = !!details

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res, vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveSuccessSnackbar(trip.id, trip.name)
    },
    onError: (_res, _vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveErrorSnackbar(trip.name)
    },
  })
  const onSuccessCreate = useCallback(
    (res: CreateItemsBatchResponse) => {
      showSaveSuccessSnackbar(res.id, res.name, res.updatedItemIds[0])
      const itemId = res.updatedItemIds[0]
      dispatch(setCurrentSectionId(itemSectionId(itemId)))
      dispatch(setTripItemHasJustBeenAdded())
    },
    [dispatch, showSaveSuccessSnackbar],
  )
  const { mutate: createItem, isLoading: isCreating } = useCreateTripItemBatch({
    onSuccess: onSuccessCreate,
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(offer.name)
    },
  })

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const newTripName = useNewTripNameFromOffer(offer)
  const createTripItems = useCallback(
    (trip: BasicTrip | FullTrip) => {
      const payload = createTourItemPayload({
        offer,
        currentRegionCode,
        currencyCode: currentCurrency,
        selectedVariation,
        templateId,
        templateItemId,
      })
      createItem({ tripId: trip.id, tripItems: [payload] })
    },
    [
      offer,
      currentRegionCode,
      currentCurrency,
      selectedVariation,
      createItem,
      templateId,
      templateItemId,
    ],
  )

  const showModal = useContext(ModalContext)
  const openSaveModal = useCallback(() => {
    return showModal<TripPlannerSaveModalResult>(
      <SaveModal
        offerType="Tour"
        defaultTripName={newTripName}
        offerImageId={getOfferImageId(offer)}
        createTripItems={createTripItems}
        isCreatingItem={isCreating}
      />,
    )
  }, [createTripItems, isCreating, newTripName, offer, showModal])

  const onSave = useCallback(() => {
    if (isSaved) {
      deleteBookmark({ tripId: details.trip.id, tripItemId: details.itemId })
    } else if (currentTrip) {
      fireInteractionEvent(
        saveToTripEvent('button', 'immersive', 'click', analyticsPage),
      )
      createTripItems(currentTrip)
    } else {
      fireInteractionEvent(
        saveToTripEvent('button', 'global', 'click', analyticsPage),
      )
      openSaveModal()
    }
  }, [
    currentTrip,
    createTripItems,
    deleteBookmark,
    isSaved,
    openSaveModal,
    details,
    analyticsPage,
  ])

  const onButtonClick = usePendingLoginHandler(
    onSave,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  return (
    <BaseBookmarkButton
      label={isSaved ? IS_SAVED_COPY : label ?? NOT_SAVED_COPY}
      isSaved={isSaved}
      isProcessing={isDeleting || isCreating}
      onClick={onButtonClick}
    />
  )
}
export default TourBookmarkButton
